import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { API } from "./utils/api";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Accounts from "./pages/Accounts";
import Investments from "./pages/Investments";
import MonthlyTracking from "./pages/MonthlyTracking";
import AnnualItems from "./pages/AnnualItems";
import FuturePlanning from "./pages/FuturePlanning";
import OnTrack from "./pages/OnTrack";
import Retirement from "./pages/Retirement";
import HolidayPlanning from "./pages/HolidayPlanning";
import Allocations from "./pages/Allocations";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";

// Configure axios defaults
axios.defaults.withCredentials = true;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);

    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];

      axios.post(`${API}/auth/session`, { session_id: sessionId })
        .then((response) => {
          // Clear the hash and navigate to dashboard with user data
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", { state: { user: response.data }, replace: true });
        })
        .catch((error) => {
          console.error("Auth error:", error);
          navigate("/", { replace: true });
        });
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone-600">Authenticating...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Otherwise, check auth with server
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Clone children and pass user prop
  return children({ user, setUser });
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Accounts user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Investments user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <MonthlyTracking user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/annual"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <AnnualItems user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <FuturePlanning user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/on-track"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <OnTrack user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/retirement"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Retirement user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/holidays"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <HolidayPlanning user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/allocations"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Allocations user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Settings user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Help user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
